import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Briefcase, Download, Eye, FileSignature, FileText, GraduationCap, PenLine, Save, Send } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardFooter, AppCardHeader, AppCardTitle, Callout, FormField,
  Input, StatusBadge, StepRail, UserAvatar,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import { formatDateTimeFriendly } from "../../utils/format";
import EducationFields from "../Students/profile/EducationFields";
import WorkHistoryEditor from "../Students/profile/WorkHistoryEditor";
import DocumentsPanel from "../Students/profile/DocumentsPanel";
import useStudentDocuments from "../Students/profile/useStudentDocuments";
import { EMPTY_OPTIONS } from "../Students/profile/useProfileOptions";
import AgreementPdfModal from "../Students/profile/AgreementPdfModal";
import { EVALUATION_STEPS, experienceMonths, stepOf, studentNextStep } from "../Students/profile/studentProfileRules";
import AgreementSignModal from "./AgreementSignModal";

const formFrom = (student = {}, workHistory = []) => ({
  degree: student.degree || "",
  degreeBranch: student.degreeBranch || "",
  college: student.college || "",
  percentage: student.percentage ?? "",
  passout: student.passout || "",
  experience: student.experience || "",
  workHistory: Array.isArray(workHistory) ? workHistory : [],
});

/**
 * ProfileWorkspace — a student's own profile beyond the admission form:
 * education and previous companies they edit, documents they upload, and —
 * as a contract student — the application they submit and the agreement they
 * acknowledge and sign.
 *
 * It is the same screen through two doors. `api` is `lmsProfileApi()` on the
 * signed-in `/s/profile`, or `linkProfileApi(token)` on the emailed no-login
 * `/profile-form`; the workspace cannot tell which. What a section may do is
 * decided by the server's `canEdit` and the module switches it answers with.
 *
 * `header(profile)` draws whatever sits above it (the LMS page header, the
 * public page's brand bar); `onLinkRefused(err)` lets the public page take
 * over when the link has died.
 */
const ProfileWorkspace = ({ api, header, focusAgreement = false, onLinkRefused }) => {
  const docs = useStudentDocuments(api.documents, true);
  const profile = docs.profile;
  const options = profile?.options || EMPTY_OPTIONS;

  const [form, setForm] = useState(formFrom());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [modal, setModal] = useState(null); // 'pdf' | 'sign'
  const [pdfKey, setPdfKey] = useState(0);
  const agreementRef = useRef(null);

  const notify = (message, type = "success") => setToast({ show: true, message, type });
  const fetchPdf = useCallback(() => api.agreement.pdfBytes(), [api]);
  const agreementApi = useMemo(() => api.agreement, [api]);

  useEffect(() => {
    if (profile && !dirty) setForm(formFrom(profile.student, profile.workHistory));
  }, [profile, dirty]);

  // The email's button lands on the agreement.
  useEffect(() => {
    if (profile && focusAgreement) agreementRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [profile, focusAgreement]);

  const patch = (changes) => { setForm((f) => ({ ...f, ...changes })); setDirty(true); };
  const months = experienceMonths(form.experience);
  const contract = !!profile?.modules?.contractStudents;
  const evaluation = profile?.evaluation || null;
  const status = evaluation?.status;
  const canEdit = profile?.canEdit !== false;
  const agreementOpen = ["sent", "student_signed", "completed"].includes(status);

  const failed = (err, fallback) => {
    if (onLinkRefused && err?.response?.status === 401) { onLinkRefused(err); return; }
    notify(err?.response?.data?.message || fallback, "error");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.update(form);
      setDirty(false);
      await docs.reload();
      notify("Profile saved.");
      return true;
    } catch (err) {
      failed(err, "Your profile could not be saved.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (dirty && !(await save())) return;
      await api.submit();
      await docs.reload();
      notify("Your profile is submitted for review.");
    } catch (err) {
      failed(err, "Your profile could not be submitted.");
    } finally {
      setSubmitting(false);
    }
  };

  if (docs.loading && !profile) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header?.(null)}
        <Shimmer height={120} count={3} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header?.(null)}
        <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={docs.reload}>Try again</AppButton>}>
          {docs.error || "Your profile could not be loaded."}
        </Callout>
      </div>
    );
  }

  const { student } = profile;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {header?.(profile)}

      <AppCard>
        <AppCardContent className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
          <UserAvatar name={student.name} size="lg" />
          <div className="tw-min-w-0 tw-flex-1">
            <h2 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content">{student.name}</h2>
            <p className="tw-m-0 tw-text-sm tw-text-content-muted">{[student.email, student.mobile].filter(Boolean).join(" · ")}</p>
          </div>
          {contract && status && <StatusBadge status={status} domain="studentEvaluation" />}
        </AppCardContent>
      </AppCard>

      {contract && (
        <AppCard ref={agreementRef} id="agreement">
          <AppCardHeader>
            <AppCardTitle className="tw-flex tw-items-center tw-gap-2"><FileSignature size={16} aria-hidden="true" /> Your application</AppCardTitle>
            <AppCardDescription>{studentNextStep(status)}</AppCardDescription>
          </AppCardHeader>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
            {status && <StepRail steps={EVALUATION_STEPS} current={stepOf(status)} reached={stepOf(status)} variant="bar" />}
            {status === "returned" && evaluation.returnNote && (
              <Callout tone="warning">From the institute: {evaluation.returnNote}</Callout>
            )}
            {agreementOpen && (
              <dl className="tw-m-0 tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-3 tw-gap-y-1 tw-text-[0.8125rem]">
                <dt className="tw-text-content-muted">Agreement</dt><dd className="tw-m-0 tw-font-mono tw-text-content">{evaluation.agreement.number}</dd>
                {evaluation.recommendation?.domains?.length > 0 && (
                  <><dt className="tw-text-content-muted">Recommended domain</dt><dd className="tw-m-0 tw-text-content">{evaluation.recommendation.domains.join(", ")}</dd></>
                )}
                {evaluation.program?.recommendedProgram && (
                  <><dt className="tw-text-content-muted">Programme</dt><dd className="tw-m-0 tw-text-content">{evaluation.program.recommendedProgram}</dd></>
                )}
                {evaluation.agreement.studentSignedAt && (
                  <><dt className="tw-text-content-muted">You signed</dt><dd className="tw-m-0 tw-text-content">{formatDateTimeFriendly(evaluation.agreement.studentSignedAt)}</dd></>
                )}
                {evaluation.agreement.instituteSignedAt && (
                  <><dt className="tw-text-content-muted">Institute signed</dt><dd className="tw-m-0 tw-text-content">{formatDateTimeFriendly(evaluation.agreement.instituteSignedAt)}</dd></>
                )}
              </dl>
            )}
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {(!status || ["returned", "void"].includes(status)) && (
                <AppButton onClick={submit} disabled={submitting || saving}>
                  <Send size={16} /> {submitting ? "Submitting…" : status === "returned" ? "Submit again" : "Submit for review"}
                </AppButton>
              )}
              {agreementOpen && (
                <AppButton variant="outline" onClick={() => setModal("pdf")}>
                  {status === "completed" ? <Download size={16} /> : <Eye size={16} />} {status === "completed" ? "Download my agreement" : "Read the agreement"}
                </AppButton>
              )}
              {status === "sent" && (
                <AppButton onClick={() => setModal("sign")}><PenLine size={16} /> Acknowledge and sign</AppButton>
              )}
            </div>
          </AppCardContent>
        </AppCard>
      )}

      {!canEdit && (
        <Callout tone="info">Your agreement has been sent, so your details and documents are locked. Ask the institute if something needs to change.</Callout>
      )}

      <div className="tw-grid tw-items-start tw-gap-4 min-[992px]:tw-grid-cols-2">
        <div className="tw-flex tw-flex-col tw-gap-4">
          <AppCard>
            <AppCardHeader>
              <AppCardTitle className="tw-flex tw-items-center tw-gap-2"><GraduationCap size={16} aria-hidden="true" /> Education</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="tw-grid tw-gap-4 min-[576px]:tw-grid-cols-2">
                <EducationFields value={form} onChange={patch} degrees={options.degrees} />
                <FormField label="Passed out year">
                  <Input value={form.passout} onChange={(e) => patch({ passout: e.target.value })} placeholder="e.g. 2024" readOnly={!canEdit} />
                </FormField>
                <FormField label="Experience" hint="Years, or e.g. 6 months — leave blank if you are a fresher">
                  <Input value={form.experience} onChange={(e) => patch({ experience: e.target.value })} placeholder="e.g. 2 years" readOnly={!canEdit} />
                </FormField>
              </div>
            </AppCardContent>
            {canEdit && (
              <AppCardFooter className="tw-flex tw-justify-end">
                <AppButton size="sm" onClick={save} disabled={!dirty || saving}><Save size={16} /> {saving ? "Saving…" : "Save"}</AppButton>
              </AppCardFooter>
            )}
          </AppCard>

          {months > 0 && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle className="tw-flex tw-items-center tw-gap-2"><Briefcase size={16} aria-hidden="true" /> Previous companies</AppCardTitle>
                <AppCardDescription>Each company, with what you did there. Save before uploading its documents.</AppCardDescription>
              </AppCardHeader>
              <AppCardContent>
                <WorkHistoryEditor rows={form.workHistory} onChange={(rows) => patch({ workHistory: rows })} readOnly={!canEdit} />
              </AppCardContent>
              {canEdit && (
                <AppCardFooter className="tw-flex tw-justify-end">
                  <AppButton size="sm" onClick={save} disabled={!dirty || saving}><Save size={16} /> {saving ? "Saving…" : "Save"}</AppButton>
                </AppCardFooter>
              )}
            </AppCard>
          )}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-4">
          <h3 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-content">
            <FileText size={16} aria-hidden="true" /> Documents
          </h3>
          <DocumentsPanel
            sections={options.documentSections}
            documents={docs.documents}
            experienceMonths={profile.experienceMonths}
            workHistory={profile.workHistory}
            accept={options.documentAccept}
            imageAccept={options.imageAccept}
            maxMb={options.documentMaxMb}
            readOnly={!canEdit}
            lockVerified
            busy={docs.busy}
            onUpload={canEdit ? docs.upload : undefined}
            onRemove={canEdit ? docs.remove : undefined}
          />
        </div>
      </div>

      <AgreementPdfModal
        show={modal === "pdf"}
        onHide={() => setModal(null)}
        fetchBytes={fetchPdf}
        reloadKey={pdfKey}
        fileName={`${evaluation?.agreement?.number || "agreement"}.pdf`}
        title={evaluation?.agreement?.number ? `Agreement ${evaluation.agreement.number}` : "Placement agreement"}
        footer={status === "sent" ? <AppButton variant="outline" onClick={() => setModal("sign")}><PenLine size={16} /> Acknowledge and sign</AppButton> : null}
      />
      <AgreementSignModal
        show={modal === "sign"}
        onHide={() => setModal(null)}
        api={agreementApi}
        onSigned={() => {
          setModal(null);
          setPdfKey((k) => k + 1);
          docs.reload();
          notify("Signed. The institute will countersign and you will get your copy by email.");
        }}
      />

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast((t) => ({ ...t, show: false }))} />
    </div>
  );
};

export default ProfileWorkspace;
